import {
  groupSubscriptionsByParent,
  ISubscriptionDraft,
  ProposedDelivery,
  Subscription,
} from '@nuts/auto-delivery-sdk';
import { defineStore } from 'pinia';
import { computed, markRaw, reactive, readonly, ref } from 'vue';

import { autoDeliveryOrderNow, init, orders as getAutoDeliveryOrders } from '@/api/autoDelivery';
import { DEFAULT_SUBSCRIPTION_CHANNEL_KEY } from '@/lib/product/prices';
import { getDeliverySubscriptionBySku, sortSubscriptionsByDate } from '@/utils/autoDelivery';
import { reportError } from '@/utils/reportError';

export interface Channel {
  key: string;
  discount: number;
}

export const useAutoDelivery = defineStore('autoDelivery', () => {
  let internalClient = markRaw(init());

  const activeSubscriptions = ref<Subscription[]>([]);

  const addedToAutoDelivery = reactive<{ deliveryId?: string; isOpen: boolean; sku?: string }>({
    deliveryId: undefined,
    isOpen: false,
    sku: undefined,
  });
  const autoDeliveryChannel = ref<Channel>();
  const autoDeliveryDefaultChannel = ref<Channel>({
    key: DEFAULT_SUBSCRIPTION_CHANNEL_KEY,
    discount: 5,
  });
  const autoDeliveryIntervalWeeks = ref([1, 2, 3, 4, 6, 8, 10, 12]);
  const inactiveSubscriptions = ref<Subscription[]>([]);
  const nextDelivery = ref<ProposedDelivery>();
  const nextShip = ref<string>();
  const orders = ref<ProposedDelivery[]>([]);
  const shippingAddressId = ref<string>();

  const client = computed<typeof internalClient>(() => internalClient);

  const initSdkClient = () => {
    internalClient = markRaw(init());
  };

  const activeSubscriptionsBySKU = computed(() => {
    const map = new Map<string, Subscription[]>();

    activeSubscriptions.value.forEach((subscription) => {
      if (map.has(subscription.sku)) {
        map.get(subscription.sku)?.push(subscription as Subscription);
      } else {
        map.set(subscription.sku, [subscription as Subscription]);
      }
    });

    return map;
  });

  // @ts-expect-error type mismatch because of protected properties on the class
  const addedToAutoDeliveryOrder = computed<ProposedDelivery | undefined>(() =>
    orders.value.find((order) => order.id === addedToAutoDelivery.deliveryId),
  );

  const addedToAutoDeliverySubscription = computed<any>(() => {
    if (!addedToAutoDeliveryOrder.value || !addedToAutoDelivery.sku) return undefined;
    return getDeliverySubscriptionBySku(addedToAutoDeliveryOrder.value, addedToAutoDelivery.sku);
  });

  const formattedAutoDeliveryIntervals = computed(() =>
    autoDeliveryIntervalWeeks.value.map((week) => ({
      value: week,
      text: `Every ${week} Week${week > 1 ? 's' : ''}`,
    })),
  );

  const nextShipOn = computed(() => orders.value[0]?.shipOn ?? nextShip.value);

  const applyShippingAddressForAllShipments = async (addressId: string) => {
    await Promise.all(orders.value.map((order) => order.update({ shippingAddressId: addressId })));
  };

  const cancelSubscription = async (
    subscription: Subscription,
    reason: string,
    cancellationReasonDetails?: string,
    sendEmailOnCancellation?: boolean,
  ) => {
    try {
      await subscription.cancel({ en: reason }, cancellationReasonDetails, sendEmailOnCancellation);
    } catch (err) {
      console.error('failed to cancel auto delivery subscription');
      console.error(err);
    }
  };

  const closeAddedToAutoDelivery = () => {
    addedToAutoDelivery.deliveryId = undefined;
    addedToAutoDelivery.isOpen = false;
    addedToAutoDelivery.sku = undefined;
  };

  const createSubscription = async (request: ISubscriptionDraft) => {
    try {
      await Subscription.create(request, internalClient, {
        retryConcurrentModification: true,
      });
    } catch (error) {
      console.error('failed to create auto delivery subscription');
      console.error(error);
      throw error;
    }
  };

  const fetchPaymentMethod = async () => {
    await internalClient.fetchPaymentMethod({ force: true });
  };

  const getActiveSubscriptions = async (
    options?: Parameters<typeof internalClient.getSubscriptions>[0],
  ) => {
    try {
      const subscriptions = await internalClient.getSubscriptions({
        force: options?.force,
        includeInactive: false,
        preloadImagesAndPaths: options?.preloadImagesAndPaths ?? false,
        preloadPaymentMethod: options?.preloadPaymentMethod ?? false,
        preloadShippingAddresses: options?.preloadShippingAddresses ?? false,
      });
      activeSubscriptions.value = groupSubscriptionsByParent(
        sortSubscriptionsByDate(subscriptions),
      );
    } catch (e) {
      reportError(e, 'Error getting active subscriptions');
    }
  };

  const getInactiveSubscriptions = async () => {
    const subscriptions = await internalClient.getSubscriptions({
      includeInactive: true,
      preloadImagesAndPaths: true,
      preloadPaymentMethod: false,
      preloadShippingAddresses: false,
    });

    inactiveSubscriptions.value = groupSubscriptionsByParent(
      subscriptions?.filter(({ active }) => active === false),
    ).filter((s) => s.variant.customAvailability?.includes('PDP'));
  };

  const getImpulseUpsellEligible = async (): Promise<boolean> => {
    try {
      await internalClient.getSubscriptions({
        force: true,
        preloadImagesAndPaths: false,
        preloadPaymentMethod: false,
        preloadShippingAddresses: false,
      });
      const delivery = await internalClient.getImpulseUpsellEligible();
      nextDelivery.value = delivery;
      if (delivery) {
        const { shipOn, shippingAddressId: deliveryShippingAddressId = '' } = delivery;
        nextShip.value = shipOn;
        shippingAddressId.value = deliveryShippingAddressId;
      }
      return Boolean(delivery);
    } catch (e) {
      reportError(e, 'Error getting impulse upsell eligible');
      return false;
    }
  };

  const getOrders = async (options?: Parameters<typeof getAutoDeliveryOrders>[1]) => {
    const { orders: userOrders } = await getAutoDeliveryOrders(internalClient, options);
    orders.value = userOrders;
    await getInactiveSubscriptions();
  };

  const openAddedToAutoDelivery = (delivery: ProposedDelivery, sku: string) => {
    addedToAutoDelivery.deliveryId = delivery.id;
    addedToAutoDelivery.sku = sku;
    addedToAutoDelivery.isOpen = true;
  };

  const reactivateSubscription = async (
    subscription: Subscription,
    targetDelivery?: ProposedDelivery,
  ) => {
    try {
      if (targetDelivery) {
        await subscription.resume({
          interval: targetDelivery.interval,
          nextShipOn: targetDelivery.shipOn,
          shippingAddressId: targetDelivery.shippingAddressId,
        });
      } else {
        await subscription.resume({ nextShipOn: nextShipOn.value });
      }
      await getOrders();
    } catch (err) {
      console.error('failed to resume auto delivery subscription');
      console.error(err);
    }
  };

  const sendDeliveryNow = async (proposedDelivery: ProposedDelivery, customerId: string) => {
    try {
      await autoDeliveryOrderNow({
        addressId: proposedDelivery.shippingAddressId,
        customerId,
        interval: proposedDelivery.interval,
        nextShipOn: proposedDelivery.shipOn,
      });
    } catch (err) {
      console.error('failed to send delivery asap');
      console.error(err);
    }
  };

  const skipDelivery = async (proposedDelivery: ProposedDelivery) => {
    try {
      orders.value = await proposedDelivery.skip();
    } catch (err) {
      console.error('failed to send delivery asap');
      console.error(err);
    }
  };

  const skipSubscription = async (subscription: Subscription) => {
    try {
      await subscription.skip();
      getOrders();
    } catch (err) {
      console.error('failed to skip subscription');
      console.error(err);
    }
  };

  const unskipSubscription = async (subscription: Subscription) => {
    await subscription.unskip();
  };

  const updateSubscription = async (
    subscription: Subscription,
    changes: Record<string, any>,
    immediateRefresh = true,
  ) => {
    try {
      await subscription.update(changes, { retryConcurrentModification: true });
      if (immediateRefresh) getOrders();
    } catch (err) {
      reportError(err, 'Failed to update auto delivery subscription');
    }
  };

  const setBackupSkuForSubscription = async (subscription: Subscription, sku: string) => {
    try {
      await subscription.setBackupSku(sku);
    } catch (err) {
      reportError(err, 'Failed to set a backup sku to the Auto-Delivery subscription');
    }
  };

  const updateEmailPreferences = async (
    options: Parameters<typeof internalClient.updateEmailPreferences>[0],
  ) => {
    await internalClient.updateEmailPreferences(options);
  };

  return {
    activeSubscriptions,
    activeSubscriptionsBySKU,
    addedToAutoDelivery,
    addedToAutoDeliveryOrder,
    addedToAutoDeliverySubscription,
    applyShippingAddressForAllShipments,
    autoDeliveryChannel: autoDeliveryChannel.value || autoDeliveryDefaultChannel.value,
    autoDeliveryDefaultChannel: readonly(autoDeliveryDefaultChannel),
    autoDeliveryIntervalWeeks,
    cancelSubscription,
    client,
    closeAddedToAutoDelivery,
    createSubscription,
    fetchPaymentMethod,
    formattedAutoDeliveryIntervals,
    getActiveSubscriptions,
    getImpulseUpsellEligible,
    getInactiveSubscriptions,
    getOrders,
    inactiveSubscriptions,
    initSdkClient,
    nextDelivery,
    nextShip,
    nextShipOn,
    openAddedToAutoDelivery,
    orders,
    reactivateSubscription,
    sendDeliveryNow,
    setBackupSkuForSubscription,
    shippingAddressId,
    skipDelivery,
    skipSubscription,
    unskipSubscription,
    updateEmailPreferences,
    updateSubscription,
  };
});
